import React, { useEffect, useState } from "react";
import { Sun, Moon, Leaf, PlayCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";
import CoreCeremony from "@/components/CoreCeremony";
import { celebrateRitualCompleted } from "@/components/SoulRitualToast";

/**
 * SkinEssentialsRitual — the Body tab's centerpiece.
 *
 * A quiet reference-ritual view: morning steps, evening steps, a dedicated
 * Face Yoga block that opens the existing CoreCeremony, and weekly add-ons.
 * Content is authored below; the Face Yoga ceremony reuses the personalized
 * plan's is_core items so completions still register in the daily plan.
 */

const MORNING = [
  {
    title: "Morning Cleanse",
    subtitle: "A mild cleanse to remove overnight oils and prepare the skin.",
    purpose: [
      "Creates a clean base for everything that follows.",
      "Supports barrier health and reduces inflammation.",
    ],
  },
  {
    title: "Vitamin C or Antioxidant Serum",
    subtitle: "Brightens, protects, and supports collagen.",
    purpose: [
      "Neutralizes environmental stressors.",
      "Supports long-term skin resilience.",
    ],
  },
  {
    title: "Moisturizer",
    subtitle: "Hydrates and strengthens the skin barrier.",
    purpose: [
      "Prevents dehydration and supports longevity of the skin.",
    ],
  },
  {
    title: "Sunscreen (SPF 30–50)",
    subtitle: "The most important longevity step.",
    purpose: [
      "Protects collagen, prevents pigmentation, slows visible aging.",
    ],
  },
];

const EVENING = [
  {
    title: "Double Cleanse",
    subtitle: "Oil cleanse → mild water-based cleanse.",
    purpose: [
      "Removes sunscreen, pollution, sweat, and buildup.",
    ],
  },
  {
    title: "Treatment Serum (Rotate Based on Needs)",
    subtitle: "Choose one:",
    options: [
      "Retinol (2–3 nights / week)",
      "Peptides",
      "Hydration serum",
      "Exfoliating serum (1–2 nights / week)",
    ],
    purpose: [
      "Supports cell turnover, firmness, and long-term skin health.",
    ],
  },
  {
    title: "Moisturizer or Barrier Cream",
    subtitle: "Seal in hydration and support overnight repair.",
    purpose: [
      "Strengthens the skin barrier and reduces inflammation.",
    ],
  },
];

const WEEKLY = [
  "Exfoliation (1–2× weekly)",
  "Hydrating mask (1× weekly)",
  "Lymphatic massage (2–3× weekly)",
  "Red light therapy (if available)",
];

export default function SkinEssentialsRitual() {
  const [plan, setPlan] = useState(null);
  const [ceremonyOpen, setCeremonyOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await endpoints.proDailyPlan();
        if (!cancelled) setPlan(data);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const coreItems = (plan?.items || []).filter((i) => i.is_core);
  const canBegin = coreItems.length > 0;

  const finishCeremony = async (completedIds) => {
    if (!plan || !completedIds?.length) return;
    try {
      await Promise.all(
        completedIds.map((iid) => endpoints.proToggleItem(plan.id, iid, true))
      );
      const { data } = await endpoints.proDailyPlan();
      setPlan(data);
      toast.success("Face cultivated.");
      celebrateRitualCompleted();
    } catch {
      toast.error("Some steps could not be saved.");
    }
  };

  return (
    <section className="space-y-10 reveal reveal-1" data-testid="skin-essentials">
      {/* Header */}
      <header>
        <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))] inline-flex items-center gap-2">
          <Sparkles size={12} /> Essentials Ritual
        </div>
        <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tight italic leading-tight">
          The essentials.
        </h2>
        <p className="mt-3 max-w-2xl text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
          When done with intention, these practices are the basis of looking and feeling your best. Every step essential to becoming your greatest self.
        </p>
      </header>

      {/* Morning */}
      <RoutineBlock
        icon={<Sun size={13} className="text-[hsl(var(--terracotta))]" />}
        eyebrow="Morning routine"
        steps={MORNING}
        testid="skin-morning"
      />

      {/* Evening */}
      <RoutineBlock
        icon={<Moon size={13} className="text-[hsl(var(--clay))]" />}
        eyebrow="Evening routine"
        steps={EVENING}
        testid="skin-evening"
      />

      {/* Face Yoga — its own quiet section, opens the ceremony */}
      <div
        className="rounded-md border hairline bg-gradient-to-br from-[hsl(var(--sand))]/50 via-[hsl(var(--paper))] to-[hsl(var(--paper))] p-7"
        data-testid="skin-face-yoga"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))] inline-flex items-center gap-2">
              <Sparkles size={12} /> Face Yoga
            </div>
            <h3 className="font-display text-2xl md:text-3xl mt-2 leading-tight text-[hsl(var(--fg))]">
              Face Yoga <span className="text-[hsl(var(--muted-foreground))] text-lg font-sans italic">(3–5 minutes)</span>
            </h3>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] max-w-xl leading-relaxed">
              <span className="font-semibold text-[hsl(var(--fg))]/80">Purpose: </span>
              Supports lymphatic flow, tone, and muscle awareness.
            </p>
          </div>

          <button
            type="button"
            disabled={!canBegin || loading}
            onClick={() => setCeremonyOpen(true)}
            data-testid="skin-face-yoga-begin"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[11px] uppercase tracking-[0.22em] bg-[hsl(var(--terracotta))] text-[hsl(var(--paper))] hover:bg-[hsl(var(--terracotta))]/90 tab-link disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlayCircle size={13} />
            {loading ? "Loading…" : "Begin the ceremony"}
          </button>
        </div>
      </div>

      {/* Weekly Add-Ons */}
      <div className="rounded-md border hairline bg-[hsl(var(--paper))] p-7" data-testid="skin-weekly">
        <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--clay))] inline-flex items-center gap-2">
          <Leaf size={12} /> Weekly add-ons <span className="normal-case tracking-normal text-[hsl(var(--muted-foreground))]">· optional</span>
        </div>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2">
          {WEEKLY.map((w) => (
            <li
              key={w}
              className="flex items-start gap-3 text-sm text-[hsl(var(--fg))]/85"
              data-testid={`skin-weekly-${w.split(" ").slice(0, 2).join("-").toLowerCase()}`}
            >
              <span aria-hidden className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full bg-[hsl(var(--clay))]" />
              <span>{w}</span>
            </li>
          ))}
        </ul>
        <p className="mt-5 text-[13px] italic text-[hsl(var(--muted-foreground))]">
          These support long-term skin resilience and recovery.
        </p>
      </div>

      <CoreCeremony
        open={ceremonyOpen}
        items={coreItems}
        onClose={() => setCeremonyOpen(false)}
        onCompletedAll={finishCeremony}
      />
    </section>
  );
}


export function RoutineBlock({ icon, eyebrow, steps, testid }) {
  return (
    <div className="rounded-md border hairline bg-[hsl(var(--paper))] p-6 md:p-7" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] inline-flex items-center gap-2">
        {icon} {eyebrow}
      </div>
      <ol className="mt-5 space-y-5">
        {steps.map((s, i) => (
          <li
            key={s.title}
            className="grid grid-cols-[auto,1fr] gap-4"
            data-testid={`${testid}-step-${i + 1}`}
          >
            <div
              aria-hidden
              className="shrink-0 grid place-items-center w-8 h-8 rounded-full bg-[hsl(var(--sand))]/60 font-display text-sm text-[hsl(var(--clay))]"
            >
              {i + 1}
            </div>
            <div className="min-w-0">
              <h4 className="font-display text-lg text-[hsl(var(--fg))] leading-snug">{s.title}</h4>
              {s.subtitle && (
                <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{s.subtitle}</p>
              )}
              {Array.isArray(s.options) && (
                <ul className="mt-2 space-y-1">
                  {s.options.map((o) => (
                    <li key={o} className="text-sm text-[hsl(var(--fg))]/85 pl-4 relative">
                      <span aria-hidden className="absolute left-0 top-2.5 w-1 h-1 rounded-full bg-[hsl(var(--muted-foreground))]/60" />
                      {o}
                    </li>
                  ))}
                </ul>
              )}
              {Array.isArray(s.purpose) && s.purpose.length > 0 && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--clay))] mb-1">Purpose</div>
                  <ul className="space-y-1">
                    {s.purpose.map((p) => (
                      <li key={p} className="text-[13px] text-[hsl(var(--fg))]/80 leading-relaxed pl-3 relative">
                        <span aria-hidden className="absolute left-0 top-2 w-1 h-1 rounded-full bg-[hsl(var(--clay))]" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
